import * as React from 'react';
import { Slider, Button, Card, Typography, Space, message, InputNumber } from 'antd';
import { BulbOutlined, CheckOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { CabinetContext } from '../../../../context/CabinetContext';
import { FORCE_DIM_ALL, TForceDimAllOutput, TForceDimAllInput } from '../../../../graphql/schema';

const { Title, Text } = Typography;

interface ILightProps {
}

const Light: React.FunctionComponent<ILightProps> = (props) => {
  const { selectedDevice } = React.useContext(CabinetContext)!;
  const [dimLevel, setDimLevel] = React.useState<number>(100);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [duration, setDuration] = React.useState<number>(30);

  const [forceDimAllMutation] = useMutation<TForceDimAllOutput, { input: TForceDimAllInput }>(
    FORCE_DIM_ALL
  );


  const handleDimLevelChange = (value: number) => {
    setDimLevel(value);
  };

  const handleDurationChange = (value: number | null) => {
    setDuration(value || 0);
  };

  const handleConfirmDim = async () => {
    if (!selectedDevice) {
      message.error("Không tìm thấy thiết bị!");
      return;
    }

    setIsLoading(true);
    
    try {
      await forceDimAllMutation({
        variables: {
          input: {
            cabinetId: selectedDevice.id,
            dimValue: dimLevel,
            duration: duration
          }
        }
      });

      const durationText = duration > 0 ? ` trong ${duration} giây` : '';
      message.success(`Đã điều chỉnh độ sáng đèn xuống ${dimLevel}%${durationText} thành công!`);
    } catch (error: any) {
      message.error(`Lỗi điều chỉnh độ sáng: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getDimLevelColor = (level: number) => {
    if (level === 0) return '#722ed1'; // Purple for off (doesn't look disabled)
    if (level >= 80) return '#52c41a'; // Green for bright
    if (level >= 50) return '#faad14'; // Orange for medium
    if (level >= 20) return '#fa8c16'; // Dark orange for dim
    return '#f5222d'; // Red for very dim
  };

  const getDimLevelText = (level: number) => {
    if (level === 0) return 'Tắt đèn';
    if (level >= 80) return 'Rất sáng';
    if (level >= 50) return 'Sáng';
    if (level >= 20) return 'Mờ';
    return 'Rất mờ';
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card 
        title={
          <Space>
            <BulbOutlined style={{ color: getDimLevelColor(dimLevel) }} />
            <Title level={4} style={{ margin: 0 }}>Điều khiển độ sáng đèn</Title>
          </Space>
        }
        className="shadow-lg"
      >
        <div className="space-y-6">
          {/* Current Status */}
          <div className="text-center bg-gray-50 rounded-lg">
            <Text strong className="text-lg">
              Độ sáng hiện tại: {dimLevel}%
            </Text>
            <div className="mt-0">
              <Text type="secondary">
                {getDimLevelText(dimLevel)}
              </Text>
            </div>
          </div>

          {/* Slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Text strong>Điều chỉnh độ sáng:</Text>
              <Text type="secondary">0% - 100%</Text>
            </div>
            
            <Slider
              min={0}
              max={100}
              value={dimLevel}
              onChange={handleDimLevelChange}
              marks={{
                0: '0%',
                25: '25%',
                50: '50%',
                75: '75%',
                100: '100%'
              }}
              tooltip={{
                formatter: (value) => `${value}%`
              }}
              trackStyle={{
                backgroundColor: getDimLevelColor(dimLevel),
                height: 8
              }}
              handleStyle={{
                borderColor: getDimLevelColor(dimLevel),
                height: 24,
                width: 8,
                marginTop: -0,
                borderRadius: 2,
                backgroundColor: getDimLevelColor(dimLevel)
              }}
              railStyle={{
                height: 8
              }}
            />
          </div>

          {/* Duration Input */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Text strong>Thời gian duy trì (giây):</Text>
              <Text type="secondary">0 = vĩnh viễn</Text>
            </div>
            
            <div className="flex items-center space-x-3">
              <ClockCircleOutlined style={{ color: '#1890ff' }} />
              <InputNumber
                min={0}
                max={3600}
                value={duration}
                onChange={handleDurationChange}
                placeholder="Nhập thời gian (giây)"
                className="flex-1"
                addonAfter="giây"
                size="large"
              />
            </div>
            
            {/* <div className="text-center bg-blue-50 rounded-lg p-3">
              <Text type="secondary" className="text-sm">
                {duration === 0 
                  ? '💡 Đèn sẽ duy trì độ sáng này vĩnh viễn'
                  : `⏱️ Đèn sẽ duy trì độ sáng ${dimLevel}% trong ${duration} giây`
                }
              </Text>
            </div> */}
          </div>

          {/* Visual Indicator */}
          {/* <div className="flex justify-center">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300"
              style={{
                backgroundColor: getDimLevelColor(dimLevel),
                opacity: dimLevel / 100 + 0.1,
                boxShadow: `0 0 20px ${getDimLevelColor(dimLevel)}40`
              }}
            >
              <BulbOutlined style={{ fontSize: '24px', color: 'white' }} />
            </div>
          </div> */}

          {/* Confirm Button */}
          <div className="flex justify-center">
            <Button
              type="primary"
              size="large"
              icon={<CheckOutlined />}
              onClick={handleConfirmDim}
              loading={isLoading}
              disabled={!selectedDevice}
              className="h-12 px-8 text-base font-medium"
              style={{
                backgroundColor: getDimLevelColor(dimLevel),
                borderColor: getDimLevelColor(dimLevel)
              }}
            >
              {dimLevel === 0 ? 'Tắt đèn' : 'Xác nhận điều chỉnh độ sáng'}
            </Button>
          </div>

          {/* Instructions */}
          <div className="text-center bg-yellow-50 rounded-lg">
            <Text type="secondary" className="text-sm">
              💡 Kéo thanh trượt để điều chỉnh độ sáng từ 0% (tắt) đến 100% (sáng nhất), 
              đặt thời gian duy trì (0 = vĩnh viễn), sau đó nhấn nút xác nhận để áp dụng thay đổi.
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Light;
